import { isPainelOficinaRoute, PAINEL_OFICINA_BASE, PAINEL_OFICINA_V2_PATH } from '@/lib/painelOficina';

/** Módulo lógico do portal usado para filtrar o menu lateral. */
export type SidebarModuleId = 'cobrancas' | 'aplicativo' | 'oficina' | 'mercado' | 'config' | null;

/**
 * A partir da rota atual, determina o módulo cujos itens devem aparecer no sidebar.
 * `null` = não filtrar por módulo (rota não mapeada: mantém o menu completo após permissões).
 */
export function getSidebarModuleFromPathname(pathname: string): SidebarModuleId {
  if (pathname === '/portal' || pathname.startsWith('/portal/')) return null;

  if (pathname === '/configuracoes/aplicativo-zehmotoca' || pathname.startsWith('/configuracoes/aplicativo-zehmotoca/')) {
    return 'aplicativo';
  }
  if (pathname === '/push-notifications') return 'aplicativo';

  if (isPainelOficinaRoute(pathname)) return 'oficina';

  if (pathname === '/mercado-zeh' || pathname.startsWith('/mercado-zeh/')) return 'mercado';

  if (
    pathname === '/email-automation' ||
    pathname === '/email-reports' ||
    pathname === '/email-ab-testing' ||
    pathname === '/email-personalization' ||
    pathname === '/email-analytics' ||
    pathname.startsWith('/email-')
  ) {
    // E-mails não têm itens dedicados no sidebar: agrupar ao hub de admin (Config).
    return 'config';
  }

  if (
    pathname === '/configuracoes' ||
    pathname === '/empresas' ||
    pathname === '/usuarios' ||
    pathname === '/importacao' ||
    pathname === '/kanban-config' ||
    pathname === '/log-acesso' ||
    pathname === '/downloads' ||
    pathname === '/whatsapp-integration'
  ) {
    return 'config';
  }

  if (
    pathname === '/dashboard' ||
    pathname === '/kanban' ||
    pathname === '/kanban-validacao' ||
    pathname === '/titulos' ||
    pathname === '/clientes-fornecedores' ||
    pathname === '/gestor-cobrancas' ||
    pathname === '/internal-chat' ||
    pathname === '/relatorios' ||
    pathname === '/whatsapp-cobrancas-report' ||
    pathname === '/operador-performance-report' ||
    pathname === '/kanban-etapas' ||
    pathname === '/test'
  ) {
    return 'cobrancas';
  }

  return null;
}

/**
 * Hrefs de itens do `Sidebar` por módulo (nomes e ordem vêm de `navigation` no Sidebar).
 * Importação e configuração de kanbans ficam no módulo Config (hub administrativo no portal).
 */
const MODULE_NAV_HREFS: Record<Exclude<SidebarModuleId, null>, Set<string>> = {
  cobrancas: new Set([
    '/titulos',
    '/clientes-fornecedores',
    '/gestor-cobrancas',
    '/internal-chat',
    '/relatorios',
    '/whatsapp-cobrancas-report',
    '/operador-performance-report',
    '/kanban-validacao',
    '/kanban-etapas',
  ]),
  aplicativo: new Set(['/configuracoes/aplicativo-zehmotoca', '/push-notifications']),
  oficina: new Set([PAINEL_OFICINA_BASE, PAINEL_OFICINA_V2_PATH]),
  mercado: new Set(['/mercado-zeh']),
  config: new Set([
    '/configuracoes',
    '/empresas',
    '/usuarios',
    '/importacao',
    '/kanban-config',
    '/log-acesso',
    '/downloads',
    '/whatsapp-integration',
  ]),
};

/** Dashboard e Kanban pertencem ao módulo Cobranças. */
export function sidebarModuleShowsDashboard(moduleId: SidebarModuleId): boolean {
  return moduleId === null || moduleId === 'cobrancas';
}

export function sidebarModuleShowsKanbanSelector(moduleId: SidebarModuleId): boolean {
  return moduleId === null || moduleId === 'cobrancas';
}

/**
 * Reduz a lista de itens de navegação ao módulo ativo. Se `moduleId` for `null`, devolve a lista inalterada.
 */
export function filterNavigationByModule<T extends { href: string }>(items: T[], moduleId: SidebarModuleId): T[] {
  if (moduleId === null) return items;
  const allow = MODULE_NAV_HREFS[moduleId];
  return items.filter((item) => allow.has(item.href));
}
